import { DDCheckList, DrilldownMappingAPIResponse, TaskNode } from '../../../types/DataDD/DataDD';
import { getDrilldownType } from '../../../Constants/Consts';

export type ChecklistDetailRow = { label: string; value: string };

const FALLBACK_LABELS = {
    dd01: 'DD01',
    dd02: 'DD02',
    dd03: 'DD03',
    dd04: 'DD04',
    dd05: 'DD05',
    modelNumber: 'Model Number',
    inspectionTask: 'Inspection Task',
} as const;

// Multi-tenant: different disciplines (mechanical, installation, etc.) use
// different drilldown types and therefore different dd01/dd02 names. When we
// don't have a resolved task to read drilldownTypeId from, derive it from the
// punch list's creator discipline via getDrilldownType().
function resolveMappingForDiscipline(
    mappings: DrilldownMappingAPIResponse[],
    disciplineId: number | null | undefined,
): DrilldownMappingAPIResponse | undefined {
    if (disciplineId == null) return mappings[0];
    const drilldownTypeId = getDrilldownType(Number(disciplineId));
    return mappings.find(m => m.drilldownTypeId === drilldownTypeId) ?? mappings[0];
}

type ChecklistTaskLike = {
    dd01?: string | null;
    dd02?: string | null;
    dd03?: string | null;
    dd04?: string | null;
    dd05?: string | null;
    modelNumber?: string | null;
    taskDescription?: string | null;
    drilldownTypeId?: number | null;
};

const DD_SLOTS = ['dd01', 'dd02', 'dd03', 'dd04', 'dd05'] as const;

// Walks the nested ChecklistsObject tree looking for a leaf with `ctUuid === target`.
// Case-insensitive UUID compare to match the rest of the app.
export function findLoadedChecklistItem(node: unknown, ctUuid: string): DDCheckList | undefined {
    if (!node || typeof node !== 'object') return undefined;
    const obj = node as Record<string, unknown>;
    for (const prop in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, prop)) continue;
        const value = obj[prop];
        if (typeof value === 'object' && value !== null) {
            const found = findLoadedChecklistItem(value, ctUuid);
            if (found) return found;
        } else if (
            prop === 'ctUuid' &&
            typeof value === 'string' &&
            value.toUpperCase() === ctUuid.toUpperCase()
        ) {
            return obj as unknown as DDCheckList;
        }
    }
    return undefined;
}

// Build the dd01-dd05 / modelNumber / inspectionTask rows for a resolved checklist
// task using the mapping that matches the *task's* drilldownTypeId. A slot is
// omitted when the mapping has no label — that signals the slot isn't part of
// the discipline's drilldown schema, so we should not render "DD0N: ..." at all.
function rowsFromTask(task: ChecklistTaskLike, mapping: DrilldownMappingAPIResponse | undefined): ChecklistDetailRow[] {
    const rows: ChecklistDetailRow[] = [];

    DD_SLOTS.forEach(slot => {
        const apiLabel = mapping?.[slot];
        if (!apiLabel) return;
        const value = task[slot] ?? null;
        rows.push({
            label: apiLabel,
            value: value == null || value === '' ? 'N/A' : String(value),
        });
    });

    rows.push({
        label: mapping?.modelNumber || FALLBACK_LABELS.modelNumber,
        value: task.modelNumber || 'N/A',
    });

    rows.push({
        label: FALLBACK_LABELS.inspectionTask,
        value: task.taskDescription || 'N/A',
    });

    return rows;
}

// Build the fallback rows (no resolved checklist task) using the drilldown
// mapping that matches the punch list's creator discipline. Each dd slot is
// only emitted if the mapping defines a label for it — skipping the row when
// the discipline doesn't use that slot (avoids "DD02: N/A" when dd02 is null).
function buildFallbackRows(
    mappings: DrilldownMappingAPIResponse[],
    disciplineId: number | null | undefined,
): ChecklistDetailRow[] {
    const mapping = resolveMappingForDiscipline(mappings, disciplineId);
    const rows: ChecklistDetailRow[] = [];

    DD_SLOTS.forEach(slot => {
        const apiLabel = mapping?.[slot];
        if (!apiLabel) return;
        rows.push({ label: apiLabel, value: 'N/A' });
    });

    rows.push({ label: mapping?.modelNumber || FALLBACK_LABELS.modelNumber, value: 'N/A' });
    rows.push({ label: FALLBACK_LABELS.inspectionTask, value: 'N/A' });

    return rows;
}

type Args = {
    checklistUuid: string | null | undefined;
    relatedLoadedChecklistDetails: TaskNode;
    relatedChecklistDetails: Array<ChecklistTaskLike & { ctUuid: string }>;
    mappings: DrilldownMappingAPIResponse[];
    adhocSequenceCa: string | null | undefined;
    adhocUnitDevice: string | null | undefined;
    // Punch list's creator discipline — used to pick the right multi-tenant
    // drilldown mapping when no task is resolved.
    disciplineId: number | null | undefined;
};

export function buildChecklistDetailRows(args: Args): ChecklistDetailRow[] {
    const { checklistUuid, relatedLoadedChecklistDetails, relatedChecklistDetails, mappings, adhocSequenceCa, adhocUnitDevice, disciplineId } = args;

    if (checklistUuid) {
        const loaded = findLoadedChecklistItem(relatedLoadedChecklistDetails, checklistUuid);
        if (loaded) {
            const mapping = mappings.find(m => m.drilldownTypeId === loaded.drilldownTypeId);
            return rowsFromTask(loaded, mapping);
        }
        const flat = relatedChecklistDetails.find(d => d.ctUuid === checklistUuid);
        if (flat) {
            const mapping = mappings.find(m => m.drilldownTypeId === flat.drilldownTypeId);
            return rowsFromTask(flat, mapping);
        }
    } else if (adhocSequenceCa) {
        // Adhoc punch lists have no linked task / drilldown type, so the dd01/dd02
        // slots carry fixed business meanings (sequence/control area and unit/device)
        // rather than mapping-driven labels.
        const adhocMapping = resolveMappingForDiscipline(mappings, disciplineId);
        return [
            { label: 'Sequence/Control Area', value: String(adhocSequenceCa) },
            { label: 'Unit/Device', value: adhocUnitDevice ? String(adhocUnitDevice) : 'N/A' },
            { label: adhocMapping?.modelNumber || FALLBACK_LABELS.modelNumber, value: 'N/A' },
            { label: FALLBACK_LABELS.inspectionTask, value: 'N/A' },
        ];
    }

    return buildFallbackRows(mappings, disciplineId);
}
